import React, { useState } from 'react';
import { Grid, Table, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { Table as TableCore } from '@devexpress/dx-react-grid';
import * as XLSX from 'xlsx';

import {
  Box,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { ImageIcon } from '../../utils/UtilsComponent';
import ExportIcon from '../../assets/icons/export-icon.svg';
import SearchIcon from '../../assets/icons/search-icon.svg';
import LocationIcon from '../../assets/icons/location-icon.svg';
import { Input } from '../../common';
import Button from '../../common/button/Button';
import { TableHeaderCell, TableHeaderContent, getTableCell, CustomFieldType } from '../../common/DxTable/DxTableCommon';
import FilterBox from './FilterBox';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CONTROL } from '../../utils/routesMap';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useAuth } from '../../hooks/useAuth';
import { useAppSelector } from '../../state/store';
import { selectReportFilters } from '../../state/modules/report/reportReducer';
import { BASE_URL } from '../../services/http.service';

interface ReportsTableProps {
  reports: any[];
  loading: boolean;
}

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${hours}:${minutes}:${seconds} ${day}/${month}/${year}`;
};

const ActionCellContent = ({
  cellProps,
  onActionClick,
}: {
  cellProps: TableCore.DataCellProps;
  onActionClick: (type: string, id: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const rowId = cellProps.row?.id;
  const locationId = cellProps.row?.location_id;

  return (
    <div>
      <IconButton
        id="action-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        id="action-menu"
        aria-labelledby="action-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {locationId && (
          <>
            <MenuItem onClick={() => onActionClick('view', rowId)} sx={{ padding: '16px' }}>
              <ListItemIcon>
                <ImageIcon image={LocationIcon} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: { fontSize: '14px' } }}>Tới vị trí trên bản đồ</ListItemText>
            </MenuItem>
            <Divider sx={{ margin: '0 16px !important' }} />
          </>
        )}
      </Menu>
    </div>
  );
};

const ReportsTable = ({ reports, loading }: ReportsTableProps) => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const { auth: { currentUser } } = useAuth();
  const currentFilters = useAppSelector(selectReportFilters);

  const filteredReports = reports.filter((report) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      (report.title || '').toLowerCase().includes(lowerSearch) ||
      (report.gateway_serial || '').toLowerCase().includes(lowerSearch)
    );
  });

  const handleExport = async () => {
    const token = localStorage.getItem('access_token');
    if (!currentUser || !token) {
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${JSON.parse(token)}`);

    const queryParams = new URLSearchParams();
    if (currentFilters.from) {
      queryParams.append('from', currentFilters.from.toString());
    }
    if (currentFilters.to) {
      queryParams.append('to', currentFilters.to.toString());
    }
    if (currentFilters.deviceType && currentFilters.deviceType !== 'all') {
      queryParams.append('deviceType', currentFilters.deviceType);
    }
    if (currentFilters.diaryType === 'disconnected') {
      queryParams.append('status', 'disconnected');
    } else if (currentFilters.diaryType && currentFilters.diaryType !== 'all') {
      queryParams.append('type', currentFilters.diaryType);
    }
    if (currentFilters.province && currentFilters.province !== 'all') {
      queryParams.append('province', currentFilters.province);
    }

    try {
      const response = await fetch(`${BASE_URL}/notifications/report/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: myHeaders,
      });

      if (!response.ok) throw new Error('Failed to fetch the CSV file');
      const csvText = await response.text();
      const workbook = XLSX.read(csvText, { type: 'string' });
      XLSX.writeFile(workbook, 'report.xlsx');
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Lỗi khi xuất file CSV', severity: 'error' });
    }
  };

  const goToMap = (id: string) => {
    const report = reports.find((item) => item.id === id);
    if (!report || !report.location_id) {
      setSnackbar({ open: true, message: 'Không tìm thấy vị trí', severity: 'error' });
      return;
    }
    navigate(`${ROUTE_CONTROL}?locationId=${report.location_id}`);
  };

  const handleClick = (type: string, id: string) => {
    if (type === 'view') {
      goToMap(id);
    }
  };

  const [columns] = useState([
    { name: 'timestamp', title: 'Thời gian' },
    { name: 'title', title: 'Tên thiết bị' },
    { name: 'gateway_serial', title: 'Serial' },
    { name: 'gateway_type_id', title: 'Loại thiết bị' },
    { name: 'province', title: 'Tỉnh'},
    { name: 'type', title: 'Loại nhật ký' },
    { name: 'status', title: 'Trạng thái' },
    { name: 'message', title: 'Nội dung nhật ký' },
    { name: 'action', title: 'Hành động' },
  ]);

  const [tableColumnExtensions] = useState<TableCore.ColumnExtension[]>([
    { columnName: 'timestamp', width: 200 },
    { columnName: 'title', width: 250 },
    { columnName: 'gateway_serial', width: 160 },
    { columnName: 'gateway_type_id', width: 180 },
    { columnName: 'province', width: 140 },
    { columnName: 'type', width: 140 },
    { columnName: 'status', width: 180 },
    { columnName: 'message', width: 600 },
    { columnName: 'action', align: 'right' },
  ]);

  const [customField] = useState<CustomFieldType>({
    timestamp: {
      renderContent: ({ row }) => <Typography sx={{ fontSize: '14px' }}>{formatTimestamp(row.timestamp)}</Typography>,
    },
    title: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.title || '--'}
        </Typography>
      ),
    },
    gateway_serial: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.gateway_serial || '--'}
        </Typography>
      ),
    },
    gateway_type_id: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.gateway_type_id || '--'}
        </Typography>
      ),
    },
    type: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.type || '--'}
        </Typography>
      ),
    },
    province: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.province || '--'}
        </Typography>
      ),
    },
    status: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.status || '--'}
        </Typography>
      ),
    },
    message: {
      renderContent: ({ row }) => (
        <Typography
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.message || '--'}
        </Typography>
      ),
    },
    action: {
      renderContent: ({ row }) => (
        <ActionCellContent
          cellProps={{ row, column: { name: 'action' } } as TableCore.DataCellProps}
          onActionClick={handleClick}
        />
      ),
    },
  });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Input
          style={{ width: 311, background: '#FFFFFF' }}
          placeholder="Tìm kiếm tên thiết bị"
          iconStartAdorment={<ImageIcon image={SearchIcon} />}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
        />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterBox />
          <Button style={{ marginLeft: 16 }} variant="contained" onClick={handleExport}>
            <ImageIcon image={ExportIcon} />
            <Typography sx={{ marginLeft: '8px' }}>Xuất Excel</Typography>
          </Button>
        </Box>
      </Box>
      <Paper sx={{ boxShadow: 'none' }}>
        <Grid rows={filteredReports} columns={columns}>
          <Table
            columnExtensions={tableColumnExtensions}
            cellComponent={(props) => getTableCell(props, null, customField)}
          />
          <TableHeaderRow cellComponent={TableHeaderCell} contentComponent={TableHeaderContent} />
        </Grid>
      </Paper>
    </>
  );
};

export default ReportsTable;
